import { TranslateService } from '@ngx-translate/core';
import { ToasterService } from './toaster.service';
import { LocationService } from './location.service';
import { Subscription, Observable, of } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { SapiService } from './sapi.service';
import { HttpClient } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';

interface RenderJob {
	id?: string;
	queued_date?: string;
	finished_date?: string;
	new_date?: string;
	running_date?: string;
	error_date?: string;
	reset_date?: string;
	hash?: string;
	renderData?: unknown;
	status?: RenderJobStatus;
}

type RenderJobStatus = 'new' | 'queued' | 'running' | 'finished' | 'error';
type RenderJobAction = 'reset' | 'queue' | 'stop' | 'run' | 'finish' | 'error';

export const jobActionToStatusMap = {
	reset: 'new',
	stop: 'new',
	queue: 'queued',
	run: 'running',
	finish: 'finished',
	error: 'error'
};

@Injectable()
export class DatafeedService implements OnDestroy {
	subscriptions = new Subscription();
	bbRenderApiBase: string;
	requestOptions: any;
	nextContinuationToken = '';
	externalRowCount = null;
	loadingRowData = false;

	constructor(
		private sapiService: SapiService,
		private toasterService: ToasterService,
		private locationService: LocationService,
		private translate: TranslateService,
		private http: HttpClient
	) {
		this.requestOptions = this.sapiService.determineRequestOptions();
		this.bbRenderApiBase = `${this.locationService.server}/render`;
		// this.bbRenderApiBase = 'http://localhost:3000/render';
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	get(id?: string) {
		return this.http.get( `${ this.bbRenderApiBase }/render-project/${id ? id : ''}`, this.requestOptions);
	}

	updateRow(datafeedId, row): Observable<any> {
		this.loadingRowData = true;
		return this.sapiService.update(`datafeed/${datafeedId}/row`, row, true).pipe(
			catchError((err) => {
				this.toasterService.error({title: this.translate.instant('ERROR_UPDATING_ROW'), message: err?.body});
				this.loadingRowData = false;
				return of(null);
			}),
			map((data) => {
				this.loadingRowData = false;
				return data;
			}));
	}

	addRows(datafeedId, rows: any[]): Observable<any> {
		this.loadingRowData = true;
		return this.sapiService.create(`datafeed/${datafeedId}/row`, rows as any, true).pipe(
			catchError((err) => {
				this.toasterService.error({title: this.translate.instant('ERROR_ADDING_ROW'), message: err?.body});
				this.loadingRowData = false;
				return of(null);
			}),
			map((data) => {
				this.loadingRowData = false;
				return data;
			}));;
	}

	deleteRows(datafeedId, rowIdArr: string[]): Observable<any> {
		if (rowIdArr?.length === 1) {
			return this.sapiService.delete('datafeed', datafeedId, `row/${ rowIdArr[0] }`);
		} else {
			// TODO
			// return this.sapiService.delete(`datafeed/${datafeedId}/row`, rowIdArr, 'action=delete');
		}
	}

	deleteAllRows(datafeedId, absolutelySure = false): Observable<any> {
		if (!datafeedId) {
			return;
		}
		return this.sapiService.postUrl(`/datafeed/${ datafeedId }/row?action=deleteAll&areYouSure=${ absolutelySure ? 'yesIAmAbsolutelySure' : 'no' }`);
	}

	getRows(datafeedId, fetchMore = false): Observable<any> {
		const limit = 15;
		this.loadingRowData = true;
		return this.sapiService.get(`datafeed/${datafeedId}/row`, this.nextContinuationToken && fetchMore
			? `?limit=${ limit }&continuationToken=${ encodeURIComponent(this.nextContinuationToken) }` : `?limit=${ limit }`)
			.pipe(
				catchError((err) => {
					this.toasterService.error({title: this.translate.instant('ERROR_FETCHING_ROWS'), message: err?.body});
					this.loadingRowData = false;
					return of(null);
				}),
				map((data: any) => {
					this.loadingRowData = false;
					this.nextContinuationToken = data?.nextContinuationToken || null;

					return data;
				})
			);
	}

	getRowById(datafeedId, rowId): Observable<any> {
		this.loadingRowData = true;
		return this.sapiService.get(`datafeed/${datafeedId}/row`, rowId).pipe(
			catchError((err) => {
				this.toasterService.error({title: this.translate.instant('ERROR_FETCHING_ROW'), message: err?.body});
				this.loadingRowData = false;
				return of(null);
			}),
			map((data) => {
				this.loadingRowData = false;
				return data;
			}));;;
	}

	getRenderJob(datafeedId: string, rowData: any) {
		if (datafeedId && rowData) {
			return this.http.get( `${ this.bbRenderApiBase }/render-project/${datafeedId}/job/${rowData.id}`, this.requestOptions);
		}
	}

	updateRenderJob(datafeedId: string, rowData: any, action: RenderJobAction): Observable<RenderJob> {
		if (!action) {
			return;
		}
		return this.http.put( `${ this.bbRenderApiBase }/render-project/${datafeedId}/job/${rowData.id}/${action}-render`, {}, this.requestOptions).pipe(
			map((data) => data as RenderJob)
		);
	}

	updateRenderProject(datafeedId: string, action: RenderJobAction): Observable<RenderJob> {
		if (!action) {
			return;
		}
		return this.http.put( `${ this.bbRenderApiBase }/render-project/${datafeedId}/${action}-render`, {}, this.requestOptions).pipe(
			map((data) => data as RenderJob)
		);
	}

	startRenderJob(datafeedId: string, rowData?: any): Observable<any> {
		if (datafeedId) {
			if (rowData) {
				return this.http.put( `${ this.bbRenderApiBase }/render-project/${datafeedId}/job/${rowData.id}/queue-render`, {}, this.requestOptions);
			} else {
				return this.http.put( `${ this.bbRenderApiBase }/render-project/${datafeedId}/queue-render`, {}, this.requestOptions);
			}
		}
	}

	stopRenderJob(datafeedId: string, rowData: any) {
		if (datafeedId) {
			if (rowData) {
				return this.http.put( `${ this.bbRenderApiBase }/render-project/${datafeedId}/job/${rowData.id}/stop-render`, {}, this.requestOptions);
			} else {
				return this.http.put( `${ this.bbRenderApiBase }/render-project/${datafeedId}/stop-render`, {}, this.requestOptions);
			}

		}
	}

	resetRenderJob(datafeedId: string, rowData: any) {
		if (datafeedId) {
			if (rowData) {
				return this.http.put( `${ this.bbRenderApiBase }/render-project/${datafeedId}/job/${rowData.id}/reset-render`, {}, this.requestOptions);
			} else {
				return this.http.put( `${ this.bbRenderApiBase }/render-project/${datafeedId}/reset-all`, {}, this.requestOptions);
			}

		}
	}

	getRenderStatus(datafeedId: string): Observable<any> {
		if (datafeedId) {
			return this.http.get( `${ this.bbRenderApiBase }/render-project/${datafeedId}/job`, this.requestOptions);
		}
	}
}
